/**
 * @fileoverview "What needs a decision" half of the WIP import review screen.
 *
 * One row per distinct name found in the file, with Morgan's proposed match
 * already selected so an accurate proposal costs the user nothing to accept.
 */

import { Badge, Input, Select } from "@/ui/primitives";
import type { BadgeColor } from "@/ui/primitives/badge";
import {
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";

/**
 * Sentinel for "no existing record". Radix treats an empty string value as
 * "nothing selected" and would render the placeholder instead of the option.
 */
export const MATCH_NONE = "__none__";

export type MatchRow = {
  key: string;
  label: string;
  detail: string;
  /** An existing record's id, or `MATCH_NONE`. */
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
  /** Wording for the `MATCH_NONE` option, e.g. `Create "ADMIN"`. */
  unmatchedLabel: string;
  unmatchedBadge: { text: string; color: BadgeColor };
  /**
   * Name to give the record being created, shown only while unmatched. Offered
   * because the file carries codes and no names: without this the firm ends up
   * with a category called `ADMIN` in every timekeeper's dropdown.
   */
  newName?: {
    value: string;
    placeholder: string;
    onChange: (v: string) => void;
  };
};

export function WipImportReviewMatches({
  title,
  description,
  rows,
  disabled,
}: {
  title: string;
  description: string;
  rows: MatchRow[];
  disabled: boolean;
}) {
  if (rows.length === 0) return null;

  return (
    <section className="rounded-xl border border-border/60 bg-background p-5">
      <h3 className="text-sm font-semibold text-primary">{title}</h3>
      <p className="mt-1 text-xs text-muted-foreground">{description}</p>

      <div className="mt-3 flex flex-col divide-y divide-border/50">
        {rows.map((row) => (
          <div key={row.key} className="py-2.5 first:pt-0 last:pb-0">
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-primary">
                  {row.label}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {row.detail}
                </p>
              </div>
              {row.value === MATCH_NONE && (
                <Badge color={row.unmatchedBadge.color} size="sm">
                  {row.unmatchedBadge.text}
                </Badge>
              )}
              <Select
                value={row.value}
                onValueChange={row.onChange}
                disabled={disabled}
              >
                <SelectTrigger className="w-60 shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={MATCH_NONE}>
                    {row.unmatchedLabel}
                  </SelectItem>
                  {row.options.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {row.value === MATCH_NONE && row.newName && (
              <div className="mt-2 flex items-center justify-end gap-2">
                <label
                  htmlFor={`${row.key}-new-name`}
                  className="text-xs text-muted-foreground"
                >
                  Call it
                </label>
                <Input
                  id={`${row.key}-new-name`}
                  className="w-60"
                  value={row.newName.value}
                  placeholder={row.newName.placeholder}
                  disabled={disabled}
                  onChange={(e) => row.newName?.onChange(e.target.value)}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
